import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService, DismissedPopupKeywords, Kindergarten, KindergartenContract, Teacher, User } from '@isophi/core-legacy';
import { ListResponseContract, TeacherContract } from '@isophi/teachers-shared';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { UserToAdd, UserToAddAlreadyExisted } from '../../modules/settings/models/user-to-add.model';
import { toSnakecaseFun } from '../utils/object.utils';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  loggedKindergarten: Kindergarten | null;
  readonly loggedKindergarten$ = new BehaviorSubject<Kindergarten | null>(null);

  selectedKindergartenUUID: string | null = null;
  private readonly kindergartenUuidKey = 'selectedKindergartenUUID';

  usersCount: number;

  private teacherSubject$ = new BehaviorSubject<void>(null);

  constructor(private httpClient: HttpClient, private authService: AuthService) {
    // Load selected kindergarten UUID from localStorage on initialization
    this.loadSelectedKindergartenUUID();
  }

  /**
   * Load selected kindergarten UUID from localStorage
   */
  private loadSelectedKindergartenUUID(): void {
    const stored = localStorage.getItem(this.kindergartenUuidKey);
    this.selectedKindergartenUUID = stored;
  }

  private isSelectedKindergartenAllowed(kindergartenUuid: string | null): boolean {
    if (!kindergartenUuid) {
      return false;
    }

    const kindergartens = this.authService.loggedUser?.teacher?.kindergartens || [];
    return kindergartens.some((kindergarten) => kindergarten.uuid === kindergartenUuid);
  }

  private normalizeSelectedKindergartenUUID(): void {
    if (!this.selectedKindergartenUUID) {
      return;
    }

    if (this.authService.loggedUser && !this.isSelectedKindergartenAllowed(this.selectedKindergartenUUID)) {
      this.selectedKindergartenUUID = null;
      this.saveSelectedKindergartenUUID();
    }
  }

  /**
   * Save selected kindergarten UUID to localStorage
   */
  private saveSelectedKindergartenUUID(): void {
    if (this.selectedKindergartenUUID) {
      localStorage.setItem(this.kindergartenUuidKey, this.selectedKindergartenUUID);
    } else {
      localStorage.removeItem(this.kindergartenUuidKey);
    }
  }

  /**
   * Get current kindergarten UUID for API headers
   */
  getCurrentKindergartenUUID(): string | null {
    this.normalizeSelectedKindergartenUUID();
    return this.selectedKindergartenUUID || this.loggedKindergarten?.uuid || null;
  }

  /**
   * Set selected kindergarten UUID
   */
  setSelectedKindergarten(kindergartenUUID: string): void {
    if (this.authService.loggedUser && !this.isSelectedKindergartenAllowed(kindergartenUUID)) {
      this.clearSelectedKindergarten();
      return;
    }

    this.selectedKindergartenUUID = kindergartenUUID;
    this.saveSelectedKindergartenUUID();
  }

  /**
   * Clear selected kindergarten UUID
   */
  clearSelectedKindergarten(): void {
    this.selectedKindergartenUUID = null;
    this.saveSelectedKindergartenUUID();
  }

  /**
   * List of teachers (Users with role teacher)
   */
  get teachers$(): Observable<User[]> {
    const url = `${environment.teacherAPI}/teachers/`;

    return this.teacherSubject$.pipe(
      distinctUntilChanged(),
      switchMap(() => {
        return this.httpClient.get<ListResponseContract<TeacherContract>>(url).pipe(
          tap((response) => (this.usersCount = response.count)),
          map((response) =>
            response.results.map((teacherData) => {
              const teacherDataSnake = toSnakecaseFun(teacherData);
              const user = User.deserialize(teacherDataSnake.user);
              Teacher.deserialize(teacherDataSnake, user);
              return user;
            })
          )
        );
      }),
      shareReplay(1)
    );
  }

  reloadTeachersList(): void {
    this.teacherSubject$.next();
  }

  /**
   * Download logged kindergarten.
   *
   * Kindergarten is downloaded during login,
   * in application use directly loggedKindergarten field.
   */
  downloadLoggedKindergarten(): Promise<void> {
    const url = `${environment.teacherAPI}/general/logged-kindergarten/`;

    return this.httpClient
      .get<KindergartenContract>(url)
      .toPromise()
      .then((response) => {
        const newKindergarten = Kindergarten.deserialize(toSnakecaseFun(response));

        this.loggedKindergarten = newKindergarten;
        this.loggedKindergarten$.next(newKindergarten);
        this.normalizeSelectedKindergartenUUID();
        if (!this.selectedKindergartenUUID && newKindergarten?.uuid) {
          this.selectedKindergartenUUID = newKindergarten.uuid;
          this.saveSelectedKindergartenUUID();
        }
        return null;
      });
  }

  dismissTeacherPopup(popup: DismissedPopupKeywords): Promise<any> {
    const url = `${environment.teacherAPI}/popup-dismissals/`;

    return firstValueFrom(this.httpClient.post(url, { popup_keyword: popup })).then(() => {
      this.authService.dismissUserPopup(popup);
    });
  }

  /**
   * Add new user
   * @param body - User details to be added
   * @returns Observable of the added user response
   */
  addUser(body: UserToAdd): Observable<UserToAddAlreadyExisted | null> {
    const url = `${environment.teacherAPI}/teachers/`;

    return this.httpClient.post<UserToAddAlreadyExisted | null>(url, body);
  }

  /**
   * Edit existing user
   * @param body - User details to be edited
   * @returns Observable of the edited user response
   */
  editUser(body: any, id: number): Observable<UserToAdd> {
    const url = `${environment.teacherAPI}/teachers/${id}/`;

    return this.httpClient.put<UserToAdd>(url, body);
  }

  /**
   * Get user by id
   *
   * @param id
   */
  getUser(id: number): Observable<User> {
    const url = `${environment.teacherAPI}/teachers/${id}/`;

    return this.httpClient.get<any>(url).pipe(
      map((teacherData) => {
        const teacherDataSnake = toSnakecaseFun(teacherData);
        const user = User.deserialize(teacherDataSnake.user);
        Teacher.deserialize(teacherDataSnake, user);
        return user;
      })
    );
  }

  /**
   * Remove user.
   *
   * @param user
   */
  public removeUser(userId: number): Observable<any> {
    const url = `${environment.teacherAPI}/teachers/${userId}/`;

    return this.httpClient.delete(url);
  }
}
